import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../api/ApiClientContext';
import { BehaviourWithHabitsResponse, UserFlowStatusResponse } from '../../api/Api';
import styles from './WelcomePage.module.scss';
import { useAuth0 } from '@auth0/auth0-react';
import React, { useState, useEffect } from 'react';
import AboutProgramStep from '../../components/welcome/AboutProgramStep';
import IntroProgramStep from '../../components/welcome/IntroProgramStep';
import HabitSelectStep from '../../components/welcome/HabitSelectStep';

import Spinner from '../../components/spinner/Spinner';
import { Dashboard } from '../../components/habitsDashboard/Dashboard/Dashboard';
import PlatformOnboarding from '../platformOnboarding/PlatformOnboarding';

interface WelcomePageProps {
  userFlowStatus?: UserFlowStatusResponse;
  onStepChange?: (step: number, showStepper: boolean) => void;
  stepClickTrigger?: number | null;
}

const WelcomePage = ({ userFlowStatus, onStepChange, stepClickTrigger }: WelcomePageProps) => {
  const queryClient = useQueryClient();
  const [selectedHabits, setSelectedHabits] = useState<string[]>([]);
  const [selectedBehaviour, setSelectedBehaviour] = useState<BehaviourWithHabitsResponse | null>(null);
  const { logout, user } = useAuth0();
  const apiClient = useApiClient();
  const [step, setStep] = useState(1);

  const { data: active } = useQuery({
    queryKey: ['myActiveHabits'],
    queryFn: async () => {
      const res = await apiClient.api.getMyActiveHabitsApiV1HabitsMyActiveGet();
      return res?.data;
    },
  });
  const { data: dashboard, isLoading: isLoadingDashboard } = useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const res = await apiClient.api.getDashboardDataApiV1DashboardGet();
      return res?.data;
    },
  });

  const { mutate: selectHabits, isPending } = useMutation({
    mutationFn: async (habitId: string) => {
      return apiClient.api.selectHabitApiV1HabitsSelectPost({
        habit_id: habitId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userFlowStatus'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  const isOnboarding = userFlowStatus?.navigation_path === 'onboarding';
  const isPlatformOnboarding = userFlowStatus?.navigation_path === 'platform_onboarding';
  const isDashboard = userFlowStatus?.navigation_path === 'dashboard';

  const pendingProgrammeId = userFlowStatus?.pending_onboarding?.programme_id;
  const pendingModuleId = userFlowStatus?.pending_onboarding?.module_id;
  const pendingModuleName = userFlowStatus?.pending_onboarding?.module_name;

  const { data: currentProgram } = useQuery({
    queryKey: ['programme', pendingProgrammeId],
    enabled: !!pendingProgrammeId && isOnboarding,
    queryFn: async () => {
      const res = await apiClient.api.getProgrammeApiV1ProgrammesProgrammeIdGet(pendingProgrammeId!);
      return res?.data;
    },
  });

  const { data: programmeBehaviours } = useQuery<BehaviourWithHabitsResponse[]>({
    queryKey: ['programmeBehaviours', pendingProgrammeId, pendingModuleId],
    enabled: !!pendingProgrammeId && isOnboarding,
    queryFn: async () => {
      const res = await apiClient.api.getProgrammeBehavioursWithHabitsApiV1ProgrammesProgrammeIdBehavioursGet(
        pendingProgrammeId!,
        pendingModuleId ? { module_id: pendingModuleId } : {},
      );
      return res?.data || [];
    },
  });

  const currentModule = currentProgram?.modules?.find((m) => m.id === pendingModuleId);
  const maxConcurrentHabits = currentModule?.max_concurrent_habits ?? 1;
  const singleBehaviour = programmeBehaviours?.length === 1;

  useEffect(() => {
    if (singleBehaviour && programmeBehaviours && !selectedBehaviour) {
      setSelectedBehaviour(programmeBehaviours[0]);
    }
  }, [singleBehaviour, programmeBehaviours, selectedBehaviour]);

  const [behaviourStep, setBehaviourStep] = useState(1);

  useEffect(() => {
    if (stepClickTrigger !== null && stepClickTrigger !== undefined) {
      if (stepClickTrigger === 1) {
        setStep(singleBehaviour ? 3 : 2);
      } else if (stepClickTrigger === 2 && selectedBehaviour) {
        setStep(3);
      }
    }
  }, [stepClickTrigger, selectedBehaviour, singleBehaviour]);

  useEffect(() => {
    if (onStepChange && isOnboarding) {
      const showStepper = step === 2 || (step === 3 && !!selectedBehaviour);
      let headerStep = 1;
      if (step === 2) {
        headerStep = 1;
      } else if (step === 3) {
        headerStep = 2;
      }
      onStepChange(headerStep, showStepper);
    }
  }, [step, behaviourStep, selectedBehaviour, isOnboarding, onStepChange]);

  const handleNextFromIntro = () => {
    if (singleBehaviour) {
      setStep(3);
    } else {
      setStep(2);
    }
  };

  const handleBackFromHabits = () => {
    if (singleBehaviour) {
      setStep(1);
    } else {
      setStep(2);
    }
  };

  const trackSelectedHabits = () => {
    selectedHabits?.forEach((habitId) => {
      selectHabits(habitId);
    });
  };

  if (isPlatformOnboarding && userFlowStatus) {
    return <PlatformOnboarding userFlowStatus={userFlowStatus} />;
  }

  if (isLoadingDashboard) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100vw',
        }}
      >
        <Spinner />
      </div>
    );
  }

  return (
    <div className={styles.welcome}>
      {isOnboarding && (
        <div className={styles.onboarding}>
          {step === 1 && (
            <IntroProgramStep
              programmeId={pendingProgrammeId}
              moduleId={pendingModuleId}
            />
          )}
          {step === 2 && (
            <AboutProgramStep
              selectedHabits={selectedHabits}
              setSelectedHabits={setSelectedHabits}
              currentStep={behaviourStep}
              onStepChange={setBehaviourStep}
              selectedBehaviour={selectedBehaviour}
              onBehaviourSelect={setSelectedBehaviour}
              programmeId={pendingProgrammeId}
              moduleId={pendingModuleId}
              moduleName={pendingModuleName}
            />
          )}
          {step === 3 && (
            <HabitSelectStep
              selectedHabits={selectedHabits}
              setSelectedHabits={setSelectedHabits}
              currentStep={behaviourStep}
              onStepChange={setBehaviourStep}
              selectedBehaviour={selectedBehaviour}
              onBack={handleBackFromHabits}
              programmeId={pendingProgrammeId}
              moduleId={pendingModuleId}
              maxConcurrentHabits={maxConcurrentHabits}
              showStepper={!singleBehaviour}
            />
          )}

          <div className={styles.welcomeFooter}>
            {step === 1 && (
              <>
                <div />
                <button className={styles.nextBtn} onClick={handleNextFromIntro}>
                  Next
                </button>
              </>
            )}
            {step === 2 && (
              <>
                <button className={styles.backBtn} onClick={() => setStep(1)}>
                  Back
                </button>
                <button
                  className={styles.nextBtn}
                  onClick={() => setStep(3)}
                  disabled={!selectedBehaviour}
                >
                  Next
                </button>
              </>
            )}
            {step === 3 && (
              <>
                <button className={styles.backBtn} onClick={handleBackFromHabits}>
                  Back
                </button>
                <button
                  className={styles.nextBtn}
                  onClick={trackSelectedHabits}
                  disabled={!selectedHabits.length || isPending}
                >
                  Next
                </button>
              </>
            )}
          </div>
        </div>
      )}
      {isDashboard && dashboard && active && <Dashboard data={dashboard} active={active} />}
    </div>
  );
};

export default WelcomePage;
